/**
 * Card for a field event summary.
 */
import * as React from 'react';
import { Calendar, Building2, BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import { EVENT_SUMMARY_STATUS_CONFIG } from './event-summary-configs';
import type { EventSummary } from '@/types/event-summary.types';
import { formatSummaryEventDate, deriveSummaryActivityName } from './event-summary-table-utils';

/** Props for {@link EventSummaryCard}. */
interface EventSummaryCardProps {
  summary: EventSummary;
  onView?: (summaryId: string) => void;
  onEdit?: () => void;
  pageActions?: { canEditRow?: boolean; canViewRow?: boolean };
}

/** Card for a field event summary. */
export const EventSummaryCard: React.FC<EventSummaryCardProps> = ({
  summary,
  onView,
  onEdit,
  pageActions,
}) => {
  const canEdit = getRowPermission(summary, 'action-edit-row', pageActions?.canEditRow ?? true);
  const canView = getRowPermission(summary, 'action-view-row', pageActions?.canViewRow ?? true);

  const statusConfig =
    EVENT_SUMMARY_STATUS_CONFIG[summary.status] || EVENT_SUMMARY_STATUS_CONFIG.not_filled;
  const StatusIcon = statusConfig.icon;

  const eventDisplayText = formatSummaryEventDate(summary);
  const activityName = deriveSummaryActivityName(summary);

  return (
    <TableCard onClick={() => canView && onView?.(summary.id)}>
      <div className="data-card-header">
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="sm"
        />
      </div>

      <div className="data-card-content space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="font-medium text-foreground text-sm">{eventDisplayText}</span>
        </div>

        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground">
            {summary.event?.establishment?.name || '—'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground">{activityName}</span>
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          {canView && (
            <Button
              preset="row-view"
              onClick={(e) => {
                e.stopPropagation();
                onView?.(summary.id);
              }}
            />
          )}
          {canEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
